/**
 * OnboardingWizard
 *
 * Shown once after registration when the teacher's profile has no state,
 * grades, or subjects. Collects teaching context and saves it via
 * updateProfile(). The teacher may skip at any time.
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, Check, X, AlertTriangle } from 'lucide-react';
import { useProfile } from './ProfileContext';
import { THEME_LIST, type TeachBudThemeId } from './theme-engine';

// ── Constants ─────────────────────────────────────────────────────────────────

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
  'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
  'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada',
  'New Hampshire','New Jersey','New Mexico','New York','North Carolina',
  'North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island',
  'South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont',
  'Virginia','Washington','West Virginia','Wisconsin','Wyoming',
  'District of Columbia',
];

const GRADE_OPTIONS = ['K','1','2','3','4','5','6','7','8'];

const SUBJECT_OPTIONS = [
  'English Language Arts','Math','Science','Social Studies',
  'Social-Emotional Learning','Special Education','Other',
];

const DURATION_OPTIONS = [
  { label: '30 minutes', value: 30 },
  { label: '45 minutes', value: 45 },
  { label: '60 minutes', value: 60 },
  { label: '90 minutes', value: 90 },
];

/**
 * States that do NOT use Common Core (CCSS).
 * TeachBud's verified standards library is CCSS-based; teachers in these
 * states should be told their state's official framework may differ.
 */
const NON_CCSS_STATES = new Set([
  'Alaska', 'Indiana', 'Nebraska', 'Oklahoma',
  'South Carolina', 'Texas', 'Virginia',
]);

const FEATURED_THEME_IDS: TeachBudThemeId[] = [
  'clean', 'elementary', 'fun', 'notebook', 'contemporary', 'chalkboard',
];

const TOTAL_STEPS = 5; // 0-indexed: 0=welcome,1=school,2=grades+subjects,3=prefs,4=done

// ── Types ─────────────────────────────────────────────────────────────────────

interface WizardData {
  school: string;
  district: string;
  state: string;
  grades: string[];
  subjects: string[];
  defaultDuration: number;
  defaultTheme: TeachBudThemeId;
}

interface Props {
  userId: string;
  onComplete: () => void;
  onSkip: () => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function toggle<T>(arr: T[], val: T): T[] {
  return arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val];
}

const slideVariants = {
  enter: (dir: number) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({ x: dir > 0 ? -60 : 60, opacity: 0 }),
};

// ── Component ─────────────────────────────────────────────────────────────────

export function OnboardingWizard({ userId, onComplete, onSkip }: Props) {
  const { updateProfile } = useProfile();
  const basePath = (import.meta.env.BASE_URL ?? '/').replace(/\/$/, '');

  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<WizardData>({
    school: '', district: '', state: '',
    grades: [], subjects: [],
    defaultDuration: 45,
    defaultTheme: 'clean',
  });

  function goNext() { setDir(1); setStep(s => Math.min(s + 1, TOTAL_STEPS - 1)); }
  function goBack() { setDir(-1); setStep(s => Math.max(s - 1, 0)); }

  function setField<K extends keyof WizardData>(key: K, val: WizardData[K]) {
    setData(prev => ({ ...prev, [key]: val }));
  }

  async function handleFinish() {
    setSaving(true);
    try {
      await updateProfile({
        school:  data.school  || undefined,
        district: data.district || undefined,
        state:   data.state   || undefined,
        grades:   data.grades,
        subjects: data.subjects,
        defaultDuration:           data.defaultDuration,
        defaultLessonTheme:        data.defaultTheme,
        defaultActivityTheme:      data.defaultTheme,
        defaultPresentationTheme:  data.defaultTheme,
        defaultAssessmentTheme:    data.defaultTheme,
        onboardingCompletedAt:     new Date().toISOString(),
      });
    } finally {
      setSaving(false);
      onComplete();
    }
  }

  const featuredThemes = THEME_LIST.filter(t => FEATURED_THEME_IDS.includes(t.id));

  const progress = step / (TOTAL_STEPS - 1);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
    >
      <motion.div
        initial={{ scale: 0.95, y: 20, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.95, y: 20, opacity: 0 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden"
      >
        {/* Progress bar */}
        <div className="h-1 bg-slate-100">
          <motion.div
            className="h-full bg-primary rounded-full"
            animate={{ width: `${progress * 100}%` }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          />
        </div>

        {/* Skip button */}
        {step < TOTAL_STEPS - 1 && (
          <div className="flex justify-end px-6 pt-4">
            <button
              onClick={onSkip}
              className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X size={13} /> Skip for now
            </button>
          </div>
        )}

        {/* Step content */}
        <div className="px-8 pb-8 pt-4 min-h-[420px] flex flex-col">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={step}
              custom={dir}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="flex-1 flex flex-col"
            >
              {/* ── Step 0: Welcome ─────────────────────────────────────────── */}
              {step === 0 && (
                <div className="flex flex-col items-center text-center flex-1 justify-center gap-6">
                  {/* Gradient border frame — black × green */}
                  <div
                    className="rounded-2xl p-[3px]"
                    style={{
                      background: 'linear-gradient(135deg, #059669 0%, #0f172a 38%, #059669 62%, #0f172a 100%)',
                      boxShadow: '0 4px 16px rgba(5,150,105,0.30), 0 2px 6px rgba(0,0,0,0.35)',
                    }}
                  >
                    <div className="bg-white rounded-[13px] p-3">
                      <img
                        src={`${basePath}/logo.png`}
                        alt="TeachBud"
                        className="h-48 w-auto"
                      />
                    </div>
                  </div>
                  <div>
                    <h2 className="text-2xl font-extrabold text-slate-900 mb-2">
                      Welcome to TeachBud!
                    </h2>
                    <p className="text-slate-500 text-sm leading-relaxed max-w-sm mx-auto">
                      Let's set up your teaching profile so TeachBud can personalize
                      every lesson, activity sheet, assessment, and presentation
                      for your classroom — automatically.
                    </p>
                  </div>
                  <p className="text-xs text-slate-400">Takes about 2 minutes</p>
                </div>
              )}

              {/* ── Step 1: School & State ───────────────────────────────────── */}
              {step === 1 && (
                <div className="flex flex-col gap-5 flex-1">
                  <div>
                    <h2 className="text-xl font-extrabold text-slate-900 mb-1">Where do you teach?</h2>
                    <p className="text-sm text-slate-500">This helps TeachBud align with your local standards.</p>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-1.5">State *</label>
                      <select
                        value={data.state}
                        onChange={e => setField('state', e.target.value)}
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                      >
                        <option value="">Select your state</option>
                        {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    {data.state && NON_CCSS_STATES.has(data.state) && (
                      <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
                        <AlertTriangle size={13} className="flex-shrink-0 mt-0.5 text-amber-500" />
                        <span>
                          <strong>{data.state} standards</strong> are not yet available in TeachBud's verified library, which is currently based on Common Core.
                          You can still use TeachBud — just enter your district's standard manually in the lesson form.
                        </span>
                      </div>
                    )}
                    <div>
                      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-1.5">School Name <span className="text-slate-400 font-normal">(optional)</span></label>
                      <input
                        type="text"
                        value={data.school}
                        onChange={e => setField('school', e.target.value)}
                        placeholder="e.g. Lincoln Elementary"
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-1.5">District <span className="text-slate-400 font-normal">(optional)</span></label>
                      <input
                        type="text"
                        value={data.district}
                        onChange={e => setField('district', e.target.value)}
                        placeholder="e.g. Austin Independent School District"
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* ── Step 2: Grades & Subjects ────────────────────────────────── */}
              {step === 2 && (
                <div className="flex flex-col gap-5 flex-1">
                  <div>
                    <h2 className="text-xl font-extrabold text-slate-900 mb-1">What do you teach?</h2>
                    <p className="text-sm text-slate-500">Select all that apply — TeachBud pre-fills these for every new resource.</p>
                  </div>
                  <div className="space-y-5">
                    <div>
                      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-2">Grade Levels *</label>
                      <div className="flex flex-wrap gap-2">
                        {GRADE_OPTIONS.map(g => {
                          const active = data.grades.includes(g);
                          return (
                            <button
                              key={g}
                              onClick={() => setField('grades', toggle(data.grades, g))}
                              className={`w-11 h-11 rounded-xl text-sm font-bold transition-all ${
                                active
                                  ? 'bg-primary text-white shadow-sm shadow-primary/20'
                                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                              }`}
                            >
                              {g}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-2">Subjects *</label>
                      <div className="flex flex-wrap gap-2">
                        {SUBJECT_OPTIONS.map(s => {
                          const active = data.subjects.includes(s);
                          return (
                            <button
                              key={s}
                              onClick={() => setField('subjects', toggle(data.subjects, s))}
                              className={`px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
                                active
                                  ? 'bg-primary text-white shadow-sm shadow-primary/20'
                                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                              }`}
                            >
                              {s}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Step 3: Teaching Preferences ─────────────────────────────── */}
              {step === 3 && (
                <div className="flex flex-col gap-5 flex-1">
                  <div>
                    <h2 className="text-xl font-extrabold text-slate-900 mb-1">Your teaching style</h2>
                    <p className="text-sm text-slate-500">TeachBud uses these as defaults — you can always change them per resource.</p>
                  </div>
                  <div className="space-y-5">
                    <div>
                      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-2">Default Lesson Duration</label>
                      <div className="grid grid-cols-4 gap-2">
                        {DURATION_OPTIONS.map(d => (
                          <button
                            key={d.value}
                            onClick={() => setField('defaultDuration', d.value)}
                            className={`py-2.5 rounded-xl text-xs font-bold transition-all ${
                              data.defaultDuration === d.value
                                ? 'bg-primary text-white shadow-sm shadow-primary/20'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                          >
                            {d.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-2">Preferred Visual Style</label>
                      <div className="grid grid-cols-3 gap-2">
                        {featuredThemes.map(theme => (
                          <button
                            key={theme.id}
                            onClick={() => setField('defaultTheme', theme.id)}
                            className={`relative p-3 rounded-xl border-2 text-left transition-all ${
                              data.defaultTheme === theme.id
                                ? 'border-primary shadow-sm'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            {data.defaultTheme === theme.id && (
                              <div className="absolute top-2 right-2 w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                                <Check size={10} className="text-white" />
                              </div>
                            )}
                            <div
                              className="h-6 rounded-lg mb-2"
                              style={{ background: `linear-gradient(135deg, ${theme.swatch[0]}, ${theme.swatch[1]})` }}
                            />
                            <span className="text-[11px] font-semibold text-slate-700 leading-tight">{theme.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Step 4: Done ─────────────────────────────────────────────── */}
              {step === 4 && (
                <div className="flex flex-col items-center text-center flex-1 justify-center gap-6">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.1 }}
                    className="w-20 h-20 rounded-full bg-emerald-50 border-4 border-emerald-200 flex items-center justify-center"
                  >
                    <Check size={36} className="text-emerald-500" strokeWidth={3} />
                  </motion.div>
                  <div>
                    <h2 className="text-2xl font-extrabold text-slate-900 mb-2">You're all set!</h2>
                    <p className="text-slate-500 text-sm leading-relaxed max-w-xs mx-auto">
                      TeachBud is now personalized for
                      {data.grades.length > 0 && data.subjects.length > 0
                        ? ` Grade${data.grades.length > 1 ? 's' : ''} ${data.grades.join(', ')} ${data.subjects.join(' & ')}`
                        : ' your classroom'}.
                      {data.state ? ` ${data.state} standards will be highlighted.` : ''}
                    </p>
                  </div>
                  <p className="text-xs text-slate-400">You can update these anytime in Settings</p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
            {step > 0 && step < TOTAL_STEPS - 1 ? (
              <button
                onClick={goBack}
                className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 font-medium transition-colors"
              >
                <ChevronLeft size={16} /> Back
              </button>
            ) : <div />}

            {step === 0 && (
              <button
                onClick={goNext}
                className="ml-auto flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl font-semibold text-sm hover:bg-primary/90 transition-all shadow-sm shadow-primary/20"
              >
                Get started <ChevronRight size={16} />
              </button>
            )}

            {step >= 1 && step < TOTAL_STEPS - 2 && (
              <button
                onClick={goNext}
                disabled={step === 1 && !data.state}
                className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl font-semibold text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm shadow-primary/20"
              >
                Continue <ChevronRight size={16} />
              </button>
            )}

            {step === TOTAL_STEPS - 2 && (
              <button
                onClick={() => { goNext(); handleFinish(); }}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl font-semibold text-sm hover:bg-primary/90 disabled:opacity-50 transition-all shadow-sm shadow-primary/20"
              >
                {saving ? 'Saving…' : <>Finish & Start Teaching <ChevronRight size={16} /></>}
              </button>
            )}

            {step === TOTAL_STEPS - 1 && (
              <button
                onClick={onComplete}
                className="ml-auto flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl font-semibold text-sm hover:bg-primary/90 transition-all shadow-sm shadow-primary/20"
              >
                Create my first lesson <ChevronRight size={16} />
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
